import DOMPurify from "dompurify";
import { twMerge } from "tailwind-merge";
import { Button } from "@/components/atoms/Button";
import type { TPlanCard } from "./types";
import { planCardVariant } from "./variables";

export const PlanCard = ({
  title,
  isMostPopular,
  description,
  value,
  creditsPerMonth,
  findEnterprise,
  buttonProps,
  initialBenefits,
  moreBenefits,
  variant,
}: TPlanCard) => {
  const {
    wrapperClass,
    titleClass,
    descriptionClass,
    moneyElement,
    creditElement,
    buttonColor,
    benefitsProps,
    dotsClass,
  } = planCardVariant[variant];

  const buttonId = "plan-init-button";

  const PlanCardElement = document.createElement("div");
  PlanCardElement.className = twMerge(
    "min-w-full w-full flex flex-col gap-6 p-6 lg:min-w-60.75",
    wrapperClass,
  );

  PlanCardElement.innerHTML = DOMPurify.sanitize(/* html */ `
        <div class="flex flex-col gap-2 h-25">
            <div class="flex items-center gap-6">
                <h2 class="${twMerge(" text-2xl font-medium", titleClass)}">
                    ${title}
                </h2>

                ${
                  isMostPopular
                    ? /* html */ `
                    <span class="py-1 px-2 bg-blue-50 rounded-md flex items-center gap-1">
                        <img src="/static/icons/blue-stars.svg" alt="icon" class="w-4 h-4 object-contain" />
    
                        <p class="text-gray-800 text-xs font-medium">MAIS POPULAR</p>
                    </span>
                `
                    : ""
                }
            </div>

            <p class="${twMerge("text-sm md:text-sm", descriptionClass)}">
                ${description}
            </p>
        </div>

        <strong class="${twMerge(
          " text-4xl font-semibold",
          moneyElement.primaryClass,
        )}">
            <span class="font-semibold text-2xl">R$</span>
             ${value}<span class="${twMerge(
               "text-gray-600 font-medium text-2xl",
               moneyElement.secondaryClass,
             )}">/mês</span>
        </strong>

        <div class="${twMerge(
          "p-1 border-2 border-dashed rounded-lg flex flex-col items-center gap-2",
          creditElement.wrapperClass,
        )}">
            <div class="flex items-center gap-2">
                <img src="${creditElement.image.src}" alt="${
                  creditElement.image.alt
                }" class="w-4.5 h-4.5 object-contain" />

                <p class="${twMerge(
                  " font-medium text-sm text-nowrap",
                  creditElement.creditClass,
                )}">
                    ${creditsPerMonth} créditos por mês
                </p>
            </div>

            <div class="${twMerge(
              "border py-1 w-[calc(100%_-_0.5rem)] rounded-md flex items-center justify-center",
              creditElement.cardClass,
            )}">
                <p class="${twMerge(
                  " font-medium text-xs lg:text-[.625rem]",
                  creditElement.enterpriseClass,
                )}">
                    ${findEnterprise}
                </p>
            </div>
        </div>

        <div id="${buttonId}"></div>

        <div class="flex flex-col gap-4">
            ${
              initialBenefits
                ? /* html */ `
                <div class="flex items-center gap-2">
                    <p class="${twMerge(
                      " text-sm",
                      benefitsProps.initialTextClass,
                    )}">Tudo do plano Grátis</p>
    
                    <img src="${benefitsProps.sumImage.src}" alt="${
                      benefitsProps.sumImage.alt
                    }" class="w-5 h-5 object-contain" />
                </div> 
            `
                : ""
            }

            ${moreBenefits
              .map(
                (benefit) => /* html */ `
                <div class="flex items-start gap-3">
                    <img src="${benefitsProps.verifiedImage.src}" alt="${
                      benefitsProps.verifiedImage.alt
                    }" class="w-5 h-5 object-contain" />
    
                    <p class="${twMerge(
                      " font-medium text-sm",
                      benefitsProps.verifiedTextClass,
                    )}">${benefit}</p>
                </div>
            `,
              )
              .join("")}
        </div>

        <div class="${twMerge(
          "hidden lg:flex items-center justify-center gap-1.25 mt-auto",
          variant === "whiteButton" && "xl:hidden",
        )}">
            <div class="${twMerge(
              "w-1.25 h-1.25 bg-gray-150",
              dotsClass,
            )}"></div>
            <div class="${twMerge(
              "w-1.25 h-1.25 bg-gray-150",
              dotsClass,
            )}"></div>
            <div class="${twMerge(
              "w-1.25 h-1.25 bg-gray-150",
              dotsClass,
            )}"></div>
        </div>
    `);

  const wrapperButton = PlanCardElement.querySelector(`#${buttonId}`);

  if (wrapperButton)
    wrapperButton.appendChild(
      Button({
        className: "w-full items-center justify-center",
        variant: buttonColor,
        text: buttonProps.text,
        onClick: () => window.open(buttonProps.link, "_blank"),
      }),
    );

  return PlanCardElement;
};
